import { View, Text, Pressable, Platform, ActivityIndicator } from 'react-native'
import { Menu, Sparkles, Palette, Users, Settings, Package, CreditCard, Lock } from 'lucide-react-native'
import type { LucideIcon } from 'lucide-react-native'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import {
  ACCOUNT_ADMIN_NAV,
  BILLING_GATE_ENABLED,
  type AccountAdminNavId,
} from '../../constants/accountAdmin.constants'
import {
  getAccountAdminNavItemsForPlatform,
  getAccountAdminNavLabel,
  getAccountAdminNavShortLabel,
} from '../../helpers/accountAdminNav.helper'
import { useAccountAdminCopy } from '../../hooks/useAccountAdminCopy'
import { AccountBrandingTab } from '../AccountBrandingTab/AccountBrandingTab'
import { AccountMembersTab } from '../AccountMembersTab/AccountMembersTab'
import { AccountSettingsTab } from '../AccountSettingsTab/AccountSettingsTab'
import { AccountBillingTab } from '../AccountBillingTab/AccountBillingTab'
import { AccountInvoiceDetailsTab } from '../AccountInvoiceDetailsTab/AccountInvoiceDetailsTab'
import { AccountAdminStartHerePanel } from '../AccountAdminStartHerePanel/AccountAdminStartHerePanel'
import type { MemberActionPending } from '../../hooks/useAccountAdminDialogs'
import type { B2bAccountUserData, B2bBrandingData, B2bMemberRow } from '../../types/b2bAccount.types'
import type { OrgInvoiceProfilePatch } from '../../helpers/orgInvoiceProfile.helper'
import type { getAccountAdminPageStyles } from '../../../../appStyles/_accountAdmin.styles'
import type { Theme } from '../../../theme/getTheme'

type BulkRemoveResult = { removed: number; skipped: { userId: string; reason: string }[] } | void

const NAV_ICONS: Record<AccountAdminNavId, LucideIcon> = {
  [ACCOUNT_ADMIN_NAV.START_HERE]: Sparkles,
  [ACCOUNT_ADMIN_NAV.BRANDING]: Palette,
  [ACCOUNT_ADMIN_NAV.MEMBERS]: Users,
  [ACCOUNT_ADMIN_NAV.SETTINGS]: Settings,
  [ACCOUNT_ADMIN_NAV.PLAN_BILLING]: Package,
  [ACCOUNT_ADMIN_NAV.INVOICE_DETAILS]: CreditCard,
}

const MOBILE_TAB_ICON_SIZE = 17
const MOBILE_TAB_ICON_STROKE = 2.25
const MOBILE_TAB_LOCK_SIZE = 11

const BILLING_LOCKED_NAV_IDS: ReadonlySet<AccountAdminNavId> = new Set([
  ACCOUNT_ADMIN_NAV.BRANDING,
  ACCOUNT_ADMIN_NAV.MEMBERS,
  ACCOUNT_ADMIN_NAV.SETTINGS,
])

interface AccountAdminTabContentProps {
  activeNav: AccountAdminNavId
  surface: 'site' | 'inApp'
  showSidebar: boolean
  siteWebOuterScroll: boolean
  user: B2bAccountUserData
  accountId: string | null
  branding: B2bBrandingData | null
  isBrandingLoading: boolean
  members: B2bMemberRow[]
  uid: string
  canLeaveOrganization: boolean
  styles: ReturnType<typeof getAccountAdminPageStyles>
  theme: Theme
  onNavigateToNav: (nav: AccountAdminNavId) => void
  onOpenNavDrawer: () => void
  onRefresh: () => Promise<void>
  patchBrandingColors: (input: { primaryColor?: string; secondaryColor?: string; displayName?: string | null }) => Promise<void>
  patchAccountSettings: (input: {
    name?: string
    isActive?: boolean
    unlimitedMembers?: boolean
    maxSeats?: number
    worldCupDefaultSquadInviteCode?: string | null
    linkedWorldCupSquadName?: string
  } & OrgInvoiceProfilePatch) => Promise<void>
  onRemoveMember: (memberUserId: string) => Promise<void>
  onPromoteToAdmin: (memberUserId: string) => Promise<void>
  onDemoteFromAdmin: (memberUserId: string) => Promise<void>
  memberActionPending: MemberActionPending | null
  onRemoveMembersBulk: (userIds: string[]) => Promise<BulkRemoveResult>
  leaveOrganization: () => Promise<void>
  deleteOrganizationPermanently: (confirmDeletionPhrase: string) => Promise<void>
  saveSsoSettings: (allowedEmailDomain: string | null) => Promise<void>
  /** True while B2B post-checkout sync + refresh run (`?billing=success`). */
  b2bStripeReturnSyncing: boolean
}

const hasTabDivider = (activeNav: AccountAdminNavId) =>
  activeNav === ACCOUNT_ADMIN_NAV.BRANDING ||
  activeNav === ACCOUNT_ADMIN_NAV.MEMBERS ||
  activeNav === ACCOUNT_ADMIN_NAV.SETTINGS ||
  activeNav === ACCOUNT_ADMIN_NAV.PLAN_BILLING ||
  activeNav === ACCOUNT_ADMIN_NAV.INVOICE_DETAILS

export function AccountAdminTabContent({
  activeNav,
  surface,
  showSidebar,
  siteWebOuterScroll,
  user,
  accountId,
  branding,
  isBrandingLoading,
  members,
  uid,
  canLeaveOrganization,
  styles,
  theme,
  onNavigateToNav,
  onOpenNavDrawer,
  onRefresh,
  patchBrandingColors,
  patchAccountSettings,
  onRemoveMember,
  onPromoteToAdmin,
  onDemoteFromAdmin,
  memberActionPending,
  onRemoveMembersBulk,
  leaveOrganization,
  deleteOrganizationPermanently,
  saveSsoSettings,
  b2bStripeReturnSyncing,
}: AccountAdminTabContentProps) {
  const copy = useAccountAdminCopy()
  const navItems = getAccountAdminNavItemsForPlatform()
  // Parent (AccountAdminScreen) only renders this once `user.account` is present
  // (guarded at its `!user.account` early-return); narrow it for the whole render.
  if (!user.account) return null
  const showDivider = hasTabDivider(activeNav)
  const billingUnlocked = !BILLING_GATE_ENABLED || (user?.account?.planTier !== 'FREE' && user?.account?.planTier !== 'STARTER') || false
  const isLockedTab = !billingUnlocked && BILLING_LOCKED_NAV_IDS.has(activeNav) || false

  return (
    <>
      {surface === 'inApp' ? (
        <>
          <View style={styles.orgToolbar}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={copy.OPEN_MENU}
              onPress={onOpenNavDrawer}
              style={({ pressed, hovered }) => [
                styles.orgToolbarMenuPressable,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.orgToolbarMenuPressed,
                hovered && Platform.OS === 'web' && styles.orgToolbarMenuHover,
              ]}
            >
              <Menu size={22} color={theme.gray[800]} strokeWidth={2.25} />
            </Pressable>
            <Text style={styles.orgToolbarTitle}>{getAccountAdminNavLabel(activeNav, copy)}</Text>
          </View>
          {showDivider ? (
            <View
              style={styles.orgTabDivider}
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
            />
          ) : null}
        </>
      ) : null}
      {surface === 'site' && !showSidebar && Platform.OS === 'web' ? (
        <View
          style={styles.mobileTabStrip}
          accessibilityRole="tablist"
        >
          <View style={styles.mobileTabStripContent}>
            {navItems.map((id) => {
              const isLocked = !billingUnlocked && BILLING_LOCKED_NAV_IDS.has(id)
              const selected = activeNav === id
              const Icon = NAV_ICONS[id]
              const label = getAccountAdminNavShortLabel(id, copy)
              const handleTabPress = () =>
                isLocked ? onNavigateToNav(ACCOUNT_ADMIN_NAV.PLAN_BILLING) : onNavigateToNav(id)
              return (
                <Pressable
                  key={id}
                  accessibilityRole="tab"
                  accessibilityState={{ selected }}
                  accessibilityLabel={getAccountAdminNavLabel(id, copy)}
                  onPress={handleTabPress}
                  style={({ pressed }) => [
                    styles.mobileTabItem,
                    selected && styles.mobileTabItemActive,
                    isLocked && styles.mobileTabItemLocked,
                    Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && { opacity: 0.8 },
                  ]}
                >
                  <Icon
                    size={MOBILE_TAB_ICON_SIZE}
                    color={isLocked ? theme.gray[400] : selected ? theme.primaryLight : theme.gray[500]}
                    strokeWidth={MOBILE_TAB_ICON_STROKE}
                  />
                  <Text
                    style={[
                      styles.mobileTabLabel,
                      selected && styles.mobileTabLabelActive,
                      isLocked && styles.mobileTabLabelLocked,
                    ]}
                  >
                    {label}
                  </Text>
                  {isLocked ? (
                    <Lock size={MOBILE_TAB_LOCK_SIZE} color={theme.gray[400]} strokeWidth={2} />
                  ) : null}
                </Pressable>
              )
            })}
          </View>
        </View>
      ) : null}
      {surface === 'site' && !showSidebar && Platform.OS !== 'web' && activeNav !== ACCOUNT_ADMIN_NAV.START_HERE ? (
        <>
          <Text style={styles.orgSiteSectionTitle}>{getAccountAdminNavLabel(activeNav, copy)}</Text>
          {showDivider ? (
            <View
              style={styles.orgTabDivider}
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
            />
          ) : null}
        </>
      ) : null}

      {b2bStripeReturnSyncing ? (
        <View
          style={styles.stripeReturnSyncingStrip}
          accessibilityRole="progressbar"
          accessibilityLabel={copy.billing.B2B_BILLING_RETURN_SYNCING_A11Y}
          accessibilityLiveRegion="polite"
        >
          <ActivityIndicator size="small" color={theme.primary} />
          <Text style={styles.stripeReturnSyncingText}>
            {copy.billing.B2B_BILLING_RETURN_SYNCING}
          </Text>
        </View>
      ) : null}

      {activeNav === ACCOUNT_ADMIN_NAV.START_HERE ? (
        <AccountAdminStartHerePanel
          onNavigateToNav={onNavigateToNav}
          embedInSiteWebOuterScroll={siteWebOuterScroll}
          joinCode={user.account.joinCode}
          worldCupSquadInviteCode={user.account.defaultWorldCupGroup?.inviteCode ?? null}
          billingUnlocked={billingUnlocked}
        />
      ) : null}

      {isLockedTab ? (
        <View style={styles.lockedTabPanel}>
          <Lock size={28} color={theme.gray[400]} strokeWidth={1.75} />
          <Text style={styles.lockedTabTitle}>
            {copy.BILLING_GATE_LOCKED_TITLE}
          </Text>
          <Text style={styles.lockedTabBody}>
            {copy.BILLING_GATE_LOCKED_BODY}
          </Text>
          <Pressable
            accessibilityRole="button"
            onPress={() => onNavigateToNav(ACCOUNT_ADMIN_NAV.PLAN_BILLING)}
            style={({ pressed, hovered }) => [
              styles.lockedTabBtn,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.lockedTabBtnPressed,
              hovered && Platform.OS === 'web' && styles.lockedTabBtnHover,
            ]}
          >
            <Text style={styles.lockedTabBtnText}>
              {copy.BILLING_GATE_GO_TO_BILLING}
            </Text>
          </Pressable>
        </View>
      ) : null}

      {!isLockedTab && activeNav === ACCOUNT_ADMIN_NAV.BRANDING ? (
        <AccountBrandingTab
          accountId={user.account.id}
          branding={branding}
          isBrandingLoading={isBrandingLoading}
          orgName={user.account.displayName ?? user.account.name}
          onRefresh={onRefresh}
          patchBrandingColors={patchBrandingColors}
          embedInSiteWebOuterScroll={siteWebOuterScroll}
        />
      ) : null}

      {!isLockedTab && activeNav === ACCOUNT_ADMIN_NAV.MEMBERS ? (
        <AccountMembersTab
          joinCode={user.account.joinCode}
          worldCupSquadInviteCode={user.account.defaultWorldCupGroup?.inviteCode ?? null}
          linkedWorldCupSquadId={user.account.defaultWorldCupGroup?.id ?? null}
          seatUsed={user.seatUsage?.used ?? 0}
          seatMax={user.seatUsage?.max ?? 0}
          planTier={user.account.planTier}
          members={members}
          currentUserId={uid}
          onRemoveMember={onRemoveMember}
          onPromoteToAdmin={onPromoteToAdmin}
          onDemoteFromAdmin={onDemoteFromAdmin}
          memberActionPending={memberActionPending}
          onRemoveMembersBulk={onRemoveMembersBulk}
          embedInSiteWebOuterScroll={siteWebOuterScroll}
        />
      ) : null}

      {activeNav === ACCOUNT_ADMIN_NAV.PLAN_BILLING ? (
        Platform.OS === 'web' ? (
          <AccountBillingTab
            accountId={accountId}
            account={user.account}
            orgName={user.account.displayName ?? user.account.name}
            theme={theme}
            stripeReturnSyncing={b2bStripeReturnSyncing}
          />
        ) : (
          <Text style={styles.sub}>{copy.PLAN_BILLING_WEB_ONLY}</Text>
        )
      ) : null}

      {activeNav === ACCOUNT_ADMIN_NAV.INVOICE_DETAILS ? (
        Platform.OS === 'web' ? (
          <AccountInvoiceDetailsTab
            accountId={accountId}
            account={user.account}
            theme={theme}
            stripeReturnSyncing={b2bStripeReturnSyncing}
            onSaveInvoiceProfile={(patch) => patchAccountSettings(patch)}
          />
        ) : (
          <Text style={styles.sub}>{copy.INVOICE_DETAILS_WEB_ONLY}</Text>
        )
      ) : null}

      {!isLockedTab && activeNav === ACCOUNT_ADMIN_NAV.SETTINGS ? (
        <AccountSettingsTab
          accountName={user.account.name}
          isAccountActive={user.account.isActive}
          canLeaveOrganization={canLeaveOrganization}
          linkedWorldCupGroup={user.account.defaultWorldCupGroup ?? null}
          allowedEmailDomain={user.account.allowedEmailDomain ?? null}
          onSaveName={(name) => patchAccountSettings({ name })}
          onSaveWorldCupLinkedSquad={(worldCupDefaultSquadInviteCode) =>
            patchAccountSettings({ worldCupDefaultSquadInviteCode })
          }
          onSaveLinkedWorldCupSquadName={(linkedWorldCupSquadName) =>
            patchAccountSettings({ linkedWorldCupSquadName })
          }
          onDeactivate={() => patchAccountSettings({ isActive: false })}
          onReactivate={() => patchAccountSettings({ isActive: true })}
          onLeaveOrganization={leaveOrganization}
          onDeleteOrganizationPermanently={deleteOrganizationPermanently}
          onSaveSsoSettings={saveSsoSettings}
          embedInSiteWebOuterScroll={siteWebOuterScroll}
        />
      ) : null}
    </>
  )
}
